import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { MediaQueryService } from '@fcom/common';
import { BoundType } from '@fcom/dapi/interfaces';
import { finShare } from '@fcom/rx';
import { isPresent, isUndefined, unsubscribe } from '@fcom/core/utils';
import { Cabin, FinnairCabinClass } from '@fcom/dapi/api/models';
import { mapLocationRouteCffDataToCabin, sortByPrice } from '@fcom/common/utils/booking.utils';
import { NotificationTheme } from '@fcom/ui-components';
import { LoaderType, MultivariateTestId, TestVariant } from '@fcom/common/interfaces';
import { ExtendedFareFamily, SortBy } from '@fcom/common-booking';
import { LocationRouteCffService } from '@fcom/core/index';
import {
  FlightSelectionFiltersComponent,
  ResetAllFiltersEvent,
  ResetFiltersEvent,
} from '@fcom/common-booking/components/flight-selection-filters/flight-selection-filters.component';
import { FlightFilters, FlightTimeOption } from '@fcom/common-booking/interfaces/flight-filters.interface';
import {
  FinnairAirBounds,
  FinnairBoundGroupWithLocation,
  SelectionPhase,
} from '@fcom/common/interfaces/booking/offers.interface';
import { AirOffersStatus } from '@fcom/common/interfaces/booking/air-offers-state.interface';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { getCabinClassesForBounds } from '@fcom/common-booking/utils/bound.utils';
import { profileType } from '@fcom/core/selectors';
import { ProfileType } from '@fcom/core-api/login';
import { CabinClassModalComponent } from '@fcom/common-booking/components/flight-selection/cabin-class-modal/cabin-class-modal.component';
import { isBoundAirDiscount } from '@fcom/common-booking/utils/common-booking.utils';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';

import { BoundsActions } from '../../../../store/actions';
import { AppConstants } from '../../../../app-constants';
import { ExtendedBoundInfo } from '../../interfaces';
import { offerListSortMap } from '../air-bounds-booking-ticket-selection-view.component';
import {
  filters,
  inbounds,
  lastRequestParams,
  nextFilteredInbounds,
  nextFilteredOutbounds,
  outbounds,
} from '../../../../store/selectors';
import { TicketSelectionParams } from '../../../../modules/ticket-selection/services';

@Component({
  selector: 'fin-optimize-air-bounds-bound-selection-list',
  styleUrls: ['./optimize-bound-selection-list.component.scss'],
  templateUrl: './optimize-bound-selection-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OptimizedAirBoundsBoundSelectionListComponent implements OnInit, OnDestroy {
  @Input()
  currencyCode$: Observable<string>;
  @Input()
  boundType: BoundType;
  @Input()
  bounds$: Observable<FinnairAirBounds>;

  /** The sort order for the bound ids. */
  @Input()
  boundIdSortOrder$: Observable<string[]>;
  @Input()
  selectedBound$: Observable<FinnairBoundGroupWithLocation>;
  @Input()
  selectedSortBy$: Observable<SortBy>;
  @Input()
  isLoading: boolean;
  @Input()
  boundsStatus$: Observable<AirOffersStatus>;
  @Input()
  selectionPhase: SelectionPhase;
  @Input()
  viewDetailsBtnKey = 'manageMyBooking.widget.viewFullOrder';
  @Input()
  showFlightSelectionFilters: boolean;

  @Output()
  openBound: EventEmitter<FinnairBoundGroupWithLocation> = new EventEmitter();
  @Output()
  selectFareFamily: EventEmitter<{
    fareFamily: ExtendedFareFamily;
    checked: boolean;
  }> = new EventEmitter();
  @Output()
  showDetails: EventEmitter<FinnairBoundGroupWithLocation> = new EventEmitter();
  @Output()
  selectSortBy: EventEmitter<SortBy> = new EventEmitter();
  @Output()
  showSpinner: EventEmitter<void> = new EventEmitter();

  @ViewChild(FlightSelectionFiltersComponent)
  private flightSelectionFilters: FlightSelectionFiltersComponent;

  @ViewChild(CabinClassModalComponent)
  private cabinClassModal: CabinClassModalComponent;

  otherBoundsList$: Observable<ExtendedBoundInfo[]>;
  bestBoundsList$: Observable<ExtendedBoundInfo[]>;
  allBoundsList$: Observable<ExtendedBoundInfo[]>;
  control: UntypedFormControl;
  openedBound$: Observable<ExtendedBoundInfo>;
  availableCabinClasses$: Observable<FinnairCabinClass[]>;
  fastestPrice$: Observable<number>;
  lowestPrice$: Observable<number>;
  isTablet$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  layoverAmounts$: Observable<number[]>;
  originLocationCode$: Observable<string>;
  destinationLocationCode$: Observable<string>;
  travelClasses$: Observable<Cabin[]>;
  filters$: Observable<FlightFilters>;
  lastRequestParams$: Observable<TicketSelectionParams>;
  outbounds$: Observable<FinnairAirBounds>;
  profileType$: Observable<ProfileType>;
  isFlightSelectionFiltersTestVariant$: Observable<boolean>;
  nextFilteredBounds$: Observable<FinnairAirBounds>;
  unfilteredBounds$: Observable<FinnairAirBounds>;
  hideFiltersAndSorter$: Observable<boolean>;
  boundsHasDirectFlights$: Observable<boolean>;
  allBoundsAreDirect$: Observable<boolean>;
  amountOfDirectFlights$: Observable<number>;

  readonly BoundType = BoundType;
  readonly AirOffersStatus = AirOffersStatus;
  readonly SortBy = SortBy;
  readonly AY_FLIGHTS_SIZE = 3;
  readonly AY_FLIGHTS_CODE = 'AY';
  readonly MAX_SPECIAL_OFFERS = 3;
  readonly MAX_RESULTS_AMOUNT_WITHOUT_FILTERS = 3;

  private subscription: Subscription = new Subscription();

  public readonly C: typeof AppConstants = AppConstants;
  public readonly NotificationTheme = NotificationTheme;
  readonly LoaderType = LoaderType;

  constructor(
    private mediaQueryService: MediaQueryService,
    private store$: Store<BookingAppState>,
    private locationRouteCffService: LocationRouteCffService,
    private multivariateTestService: MultivariateTestService
  ) {}

  ngOnInit(): void {
    this.control = new UntypedFormControl('');

    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.isTablet$ = this.mediaQueryService.isBreakpoint$('tablet_down');

    this.filters$ = this.store$.pipe(filters());
    this.lastRequestParams$ = this.store$.pipe(lastRequestParams());

    this.nextFilteredBounds$ = combineLatest([
      this.store$.pipe(nextFilteredOutbounds()),
      this.store$.pipe(nextFilteredInbounds()),
    ]).pipe(
      map(([filteredOutbounds, filteredInbounds]) =>
        this.boundType === BoundType.inbound ? filteredInbounds : filteredOutbounds
      )
    );

    this.unfilteredBounds$ = combineLatest([this.store$.pipe(outbounds()), this.store$.pipe(inbounds())]).pipe(
      map(([unfilteredOutbounds, unfilteredInbounds]) =>
        this.boundType === BoundType.inbound ? unfilteredInbounds : unfilteredOutbounds
      ),
      filter(Boolean)
    );

    this.isFlightSelectionFiltersTestVariant$ = this.multivariateTestService
      .getTestVariant(MultivariateTestId.FLIGHT_SELECTION_FILTERS)
      .pipe(
        map(({ variant }) => variant === TestVariant.B),
        finShare()
      );

    this.hideFiltersAndSorter$ = combineLatest([
      this.isFlightSelectionFiltersTestVariant$,
      this.unfilteredBounds$,
    ]).pipe(
      map(
        ([isTestVariant, bounds]) =>
          isTestVariant && bounds.boundGroups.length <= this.MAX_RESULTS_AMOUNT_WITHOUT_FILTERS
      )
    );

    this.boundsHasDirectFlights$ = this.unfilteredBounds$.pipe(
      map((bounds) => bounds.boundGroups.some((bound) => bound.details.stops === 0))
    );

    this.allBoundsAreDirect$ = this.unfilteredBounds$.pipe(
      map((bounds) => bounds.boundGroups.every((bound) => bound.details.stops === 0))
    );

    this.amountOfDirectFlights$ = this.unfilteredBounds$.pipe(
      map((bounds) => bounds.boundGroups.filter((bound) => bound.details.stops === 0).length)
    );

    this.layoverAmounts$ = this.bounds$.pipe(map((bounds) => bounds.boundGroups.map((obj) => obj.details.stops)));
    this.openedBound$ = this.control.valueChanges.pipe(filter(isPresent), distinctUntilKeyChanged('sortingId'));

    const boundIndex = this.boundType === BoundType.outbound ? 0 : 1;
    this.originLocationCode$ = this.bounds$.pipe(
      map((bounds) => bounds.request.itineraries[boundIndex].departureLocationCode)
    );
    this.destinationLocationCode$ = this.bounds$.pipe(
      map((bounds) => bounds.request.itineraries[boundIndex].destinationLocationCode)
    );

    this.availableCabinClasses$ = this.bounds$.pipe(map((bounds) => getCabinClassesForBounds(bounds)));

    this.profileType$ = this.store$.pipe(profileType());
    this.travelClasses$ = this.store$.pipe(
      outbounds(),
      take(1),
      map((outboundsResult) => {
        if (!outboundsResult?.request?.itineraries) {
          return null;
        }

        const itinerary = outboundsResult.request.itineraries;

        const originLocationCode = itinerary[0].departureLocationCode;
        const destinationLocationCode = itinerary[0].destinationLocationCode;

        return { originLocationCode, destinationLocationCode };
      }),
      withLatestFrom(this.profileType$),
      switchMap(([locations, userProfileType]) => {
        if (!locations) {
          return of([]);
        }
        const { destinationLocationCode, originLocationCode } = locations;
        return this.locationRouteCffService
          .routeCffsFor(
            originLocationCode,
            destinationLocationCode,
            undefined,
            undefined,
            String(userProfileType === ProfileType.CORPORATE)
          )
          .pipe(
            take(1),
            map((result) => mapLocationRouteCffDataToCabin(result))
          );
      }),
      finShare()
    );

    const sortedBounds$: Observable<FinnairBoundGroupWithLocation[]> = combineLatest([
      this.bounds$.pipe(filter(isPresent)),
      this.boundIdSortOrder$.pipe(filter(Boolean)),
      this.selectedSortBy$,
    ]).pipe(
      map(
        ([bounds, sortOrder, selectedSortBy]: [
          FinnairAirBounds,
          string[],
          SortBy,
        ]): FinnairBoundGroupWithLocation[] => {
          const results = sortOrder
            .map((id: string) => bounds.boundGroups.find((b) => b.sortingId === id))
            .filter(Boolean)
            .filter((boundGroup) => boundGroup.fareFamilies.length > 0);

          let sorted = results;
          if (selectedSortBy === SortBy.PRICE) {
            sorted = sortByPrice(results);
          }

          return sorted;
        }
      ),
      finShare()
    );

    const fastestSortKey = offerListSortMap[SortBy.DURATION];
    let lowestPrice: number;
    this.fastestPrice$ = this.bounds$.pipe(
      filter(isPresent),
      map((bound) => {
        let currentBoundGroupsLowestPrice;
        for (const sortingId of bound.sorting.boundGroups[fastestSortKey]) {
          currentBoundGroupsLowestPrice = bound.boundGroups
            .find((group) => group.sortingId === sortingId)
            ?.fareFamilies.reduce<number>((min, ff) => {
              const numericPrice = parseFloat(ff.price);
              return !min || numericPrice < min ? numericPrice : min;
            }, null);

          if (currentBoundGroupsLowestPrice) {
            lowestPrice = currentBoundGroupsLowestPrice;
            // We want to break the for loop because found the lowest price of the fastest boundGroup
            break;
          }
        }

        return lowestPrice;
      })
    );

    this.lowestPrice$ = combineLatest([
      this.bounds$.pipe(filter(isPresent)),
      this.boundIdSortOrder$.pipe(filter(Boolean)),
    ]).pipe(
      map(([bounds, sortOrder]: [FinnairAirBounds, string[]]) => {
        const results = sortOrder
          .map((id: string) => bounds.boundGroups.find((b) => b.sortingId === id))
          .filter(Boolean);

        const sorted = sortByPrice(results);

        return sorted[0]?.fareFamilies.reduce<number>((min, ff) => {
          const numericPrice = parseFloat(ff.price);
          return !min || numericPrice < min ? numericPrice : min;
        }, null);
      }),
      finShare()
    );

    const selectedBoundId$: Observable<string> = this.selectedBound$.pipe(
      filter(isPresent),
      map(({ sortingId }): string => sortingId),
      distinctUntilChanged()
    );

    this.subscription.add(
      this.openedBound$
        .pipe(map(({ ...bound }: ExtendedBoundInfo) => bound))
        .subscribe((bound: FinnairBoundGroupWithLocation) => {
          this.openBound.emit(bound);
        })
    );

    this.subscription.add(
      selectedBoundId$
        .pipe(
          withLatestFrom(sortedBounds$),
          map(
            ([selected, bounds]: [string, FinnairBoundGroupWithLocation[]]): FinnairBoundGroupWithLocation =>
              bounds.find(({ sortingId }: ExtendedBoundInfo) => sortingId === selected)
          )
        )
        .subscribe((selectedBound: FinnairBoundGroupWithLocation) => {
          this.control.setValue(selectedBound);
        })
    );

    const specialOffers$ = sortedBounds$.pipe(
      map((bounds) => {
        const offers = bounds.filter((bound) => bound.hasSpecialOffers || isBoundAirDiscount(bound));
        return offers.length < this.MAX_SPECIAL_OFFERS && offers.length !== 0 ? offers : null;
      })
    );

    this.bestBoundsList$ = combineLatest([
      sortedBounds$,
      selectedBoundId$.pipe(startWith(undefined as string)),
      this.openedBound$.pipe(
        map((bound: ExtendedBoundInfo) => bound.sortingId),
        startWith(undefined as string)
      ),
      specialOffers$,
    ]).pipe(
      map(([bounds, selectedBoundId, openedBoundId, specialOffers]): ExtendedBoundInfo[] => {
        if (bounds.length === 0) {
          return [];
        }

        const specialCounties = ['US', 'GB', 'JP'];
        // check if there are any US, GB or JP locations
        const isUSorGBorJP = bounds.some((bound) =>
          specialCounties.includes(bound.details.arrival.countryCode || bound.details.departure.countryCode)
        );
        let directBounds;
        // if found any of US, UK or JP locations, then get any direct flights first
        if (isUSorGBorJP) {
          const directBoundsList = bounds.filter((bound) => bound.details.stops === 0).slice(0, this.AY_FLIGHTS_SIZE);
          directBounds = directBoundsList;
        } else {
          // if not found any of US, UK or JP locations, then show the first 3 AY best bounds
          // Max 3 AY bounds
          const ayBounds: FinnairBoundGroupWithLocation[] = bounds
            .filter((bound: FinnairBoundGroupWithLocation) =>
              bound.details.operatingAirlineCodes.every((code) => code === this.AY_FLIGHTS_CODE)
            )
            .slice(0, this.AY_FLIGHTS_SIZE);
          directBounds = ayBounds;
        }

        if (directBounds.length < this.AY_FLIGHTS_SIZE) {
          directBounds = directBounds
            .concat(
              bounds.filter(
                (bound: FinnairBoundGroupWithLocation) =>
                  bound.details.operatingAirlineCodes.some((code) => code !== this.AY_FLIGHTS_CODE) &&
                  bound.details.stops === 0
              )
            )
            .slice(0, this.AY_FLIGHTS_SIZE);
        }

        // 1 cheapest bound
        const cheapestBoundList: FinnairBoundGroupWithLocation[] = [...bounds].sort(
          (a, b) => Number(a.cheapestPrice) - Number(b.cheapestPrice)
        );

        // check if cheapest bound is not include in the ay bounds
        if (directBounds.find((bound) => bound.sortingId === cheapestBoundList[0].sortingId)) {
          return this.generateBoundInfo(directBounds, selectedBoundId, openedBoundId, specialOffers);
        }
        return this.generateBoundInfo(
          directBounds.concat([cheapestBoundList[0]]),
          selectedBoundId,
          openedBoundId,
          specialOffers
        );
      }),
      finShare()
    );

    this.otherBoundsList$ = combineLatest([
      this.bestBoundsList$,
      sortedBounds$,
      selectedBoundId$.pipe(startWith(undefined as string)),
      this.openedBound$.pipe(
        map((bound: ExtendedBoundInfo) => bound.sortingId),
        startWith(undefined as string)
      ),
      specialOffers$,
    ]).pipe(
      map(([bestbounds, bounds, selectedBoundId, openedBoundId, specialOffers]): ExtendedBoundInfo[] => {
        const bestboundsIds = bestbounds.map(({ sortingId }) => sortingId);
        return this.generateBoundInfo(
          bounds.filter((bound) => !bestboundsIds.includes(bound.sortingId)),
          selectedBoundId,
          openedBoundId,
          specialOffers
        );
      }),
      finShare()
    );

    this.allBoundsList$ = combineLatest([
      sortedBounds$,
      selectedBoundId$.pipe(startWith(undefined as string)),
      this.openedBound$.pipe(
        map((bound: ExtendedBoundInfo) => bound.sortingId),
        startWith(undefined as string)
      ),
      specialOffers$,
    ]).pipe(
      map(([bounds, selectedBoundId, openedBoundId, specialOffers]): ExtendedBoundInfo[] => {
        return this.generateBoundInfo(bounds, selectedBoundId, openedBoundId, specialOffers);
      }),
      finShare()
    );
  }

  private generateBoundInfo(
    bounds: FinnairBoundGroupWithLocation[],
    selectedBoundId: string,
    openedBoundId: string,
    specialOffers: FinnairBoundGroupWithLocation[]
  ): ExtendedBoundInfo[] {
    if (bounds.length === 0) {
      return [];
    }

    return bounds.map((bound: FinnairBoundGroupWithLocation) => ({
      ...bound,
      selected: bound.sortingId === selectedBoundId,
      open: bound.sortingId === openedBoundId,
      showSpecialOfferBadge: specialOffers?.some((offer) => offer.sortingId === bound.sortingId),
    }));
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  openCabinClassModal(cabinClass: string): void {
    this.cabinClassModal.openModal(null, cabinClass.toLowerCase());
  }

  onSelectSortBy(data: SortBy): void {
    this.selectSortBy.emit(data);
  }

  onSetFilter(flightFilters: FlightFilters): void {
    const filteredFilters = Object.entries(flightFilters)
      .filter(([_, value]) => !isUndefined(value))
      .reduce((obj, [filterName, value]) => ({ ...obj, [filterName]: value }), {});

    Object.entries(filteredFilters).forEach(([filterName, value]) => {
      switch (filterName as keyof FlightFilters) {
        case 'stopsCount':
          this.store$.dispatch(BoundsActions.setStopsCount({ stopsCount: value as number | null }));
          break;

        case 'maxStops':
          this.store$.dispatch(BoundsActions.setMaxStops({ maxStops: value as number | null }));
          break;

        case 'cabin':
          this.store$.dispatch(BoundsActions.setCabin({ cabin: value as Cabin }));
          break;

        case 'refundable':
          this.store$.dispatch(BoundsActions.setRefundable({ refundable: value as boolean }));
          break;

        case 'departureTime':
          this.store$.dispatch(BoundsActions.setDepartureTime({ departureTime: value as FlightTimeOption[] }));
          break;

        case 'arrivalTime':
          this.store$.dispatch(BoundsActions.setArrivalTime({ arrivalTime: value as FlightTimeOption[] }));
          break;

        default:
          break;
      }
    });
  }

  onResetFilter(event: ResetFiltersEvent): void {
    switch (event.type) {
      case 'stopsCount':
        this.store$.dispatch(BoundsActions.resetStopsCount());
        break;
      case 'cabin':
        this.store$.dispatch(BoundsActions.resetCabin({ cabin: event.cabin }));
        break;
      case 'refundable':
        this.store$.dispatch(BoundsActions.resetRefundable());
        break;

      case 'departureTime':
        this.store$.dispatch(BoundsActions.resetDepartureTime());
        break;

      case 'arrivalTime':
        this.store$.dispatch(BoundsActions.resetArrivalTime());
        break;

      default:
        break;
    }
  }

  onResetAllFilters(event: ResetAllFiltersEvent): void {
    this.store$.dispatch(
      BoundsActions.resetAllFilters({
        keepCabin: this.flightSelectionFilters.shouldNotClearTravelClass,
        defaultCabin: event.defaultCabin,
      })
    );
  }

  clearAllFilters(): void {
    this.flightSelectionFilters.clearAllFilters('empty-state');
  }
}
